import { useAsyncExecutor, useAsyncExecutor2 } from "async-executor-hook";
import { format } from "date-fns";
import moment from "moment-timezone";
import { Checkbox } from "primereact/checkbox";
import { Paginator } from "primereact/paginator";
import { parse, stringify } from "query-string";
import { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  combineLatest,
  filter,
  from,
  interval,
  map,
  mapTo,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from "rxjs";

import { Authorization } from "../../../common/AuthorizationCpt";
import { ModalSuccessConfirmation } from "../../../common/components/ModalSuccessConfirmation/ModalSuccessConfirmation";
import { DivCheckbox } from "../../../common/components/PaymentQuotedTableStyles";
import { StatusCommon } from "../../../common/components/StatusCommon/StatusCommon";
import { HTTP_METHODS } from "../../../utils/constants";
import { dateToRegularFormat } from "../../../utils/dates";
import { networkBackendRequest } from "../../../utils/networkUtils";
import { formatMoney } from "../../../utils/numbers";
import { USER_ROUTES } from "../../../utils/routeConstants";
import CommentModal from "../../CommentModal/CommentModal";
import ConfirmationModal from "../../ConfirmationModal/ConfirmationModal";
import { CurrencyContext } from "../../CurrencyContext/CurrencyContext";
import { CommentButton, DeleteButton } from "../../IconButtons";
import {
  ColoredTag,
  InvoiceContainer,
  VolanteId,
} from "../../Invoice/InvoiceStyles";
import { InvoicesContainer, TableHeader } from "../../Invoices/InvoicesStyles";
import PaymentCommentModal from "../../PaymentCommentModal/PaymentCommentModal";
import PaymentDeletionModal from "../../PaymentDeletionModal/PaymentDeletionModal";
import PaymentLoader from "../../PaymentLoader/PaymentLoader";
import PaymentModal from "../../PaymentModal/PaymentModal";
import StandaloneMessage from "../../StandaloneMessage/StandaloneMessage";
import { paymentQuotedConvertResponse, POLLING_INTERVAL } from "../common";
import { StatusSectionDetails } from "../common/PaymentTableStatus";
import {
  getPaymentTableHeaders,
  ITEMS_PER_PAGE,
  PAYMENT_TABLE_MODAL_STATES,
} from "../common/utils";
import {
  ButtonContainer,
  Container,
  MainButton,
  ProcessingColum,
} from "../PaymentStyles";
import { PaymentQuotedSidebarStatus } from "./PaymentQuotedSidebarStatus";

import GifPending from "../../../images/gif_waiting_processing.gif";

export const PaymentQuotedTable = (props) => {
  const { params$, shoppingCar, clearShoppingCar } = props;

  const destroy$Ref = useRef(new Subject());
  const destroy$: any = destroy$Ref.current;

  useEffect(() => {
    return () => {
      destroy$.next();
      destroy$.complete();
    };
  }, [destroy$]);

  const [dateUrlQuotationQuoted, setDateUrlQuotationQuoted] = useState(
    new Date()
  );
  const loadInvoices = useAsyncExecutor2(
    (params) => {
      const destroy$ = destroy$Ref.current;
      return interval(POLLING_INTERVAL).pipe(
        takeUntil(destroy$),
        startWith(params),
        mapTo(params),
        switchMap((params: any) => {
          setDateUrlQuotationQuoted(params.from_date);
          const request = networkBackendRequest(
            `/quotations/quoted/?${stringify(params)}`,
            HTTP_METHODS.GET,
            undefined,
            undefined
          )
            .then((response) => response.json())
            .then(paymentQuotedConvertResponse);
          return from(request);
        })
      );
    },
    {
      params$: params$.pipe(
        takeUntil(destroy$Ref.current),
        map((params: any) => ({
          ...params,
          from_date: format(params.from_date, "yyyy-MM-dd"),
          to_date: format(params.to_date, "yyyy-MM-dd"),
          order: "descending",
          page: params.page ?? 1,
          page_size: ITEMS_PER_PAGE,
          payment_status_list: "quoted",
        }))
      ),
    }
  );

  const [invoicesStatus, setInvoicesStatus] = useState<any>({
    status: "",
    data: { details: [], num_page: 0, page_size: 0, params: {} },
  });

  const [total, setTotal] = useState({ pen: 0, usd: 0 });

  useEffect(() => {
    const checkedInvoices$ = combineLatest(
      loadInvoices.state$,
      shoppingCar.items.valueChanges.pipe(startWith(shoppingCar.items.value)),
      (invoicesStatus, addedItems: any) => {
        if (invoicesStatus.status !== "SUCCESS") return invoicesStatus;
        const invoices = invoicesStatus.data;
        const details = invoices.details.map((invoice) => {
          const checked = addedItems.some((x) => x.id === invoice.id);
          return { checked, invoice };
        });
        return { ...invoicesStatus, data: { ...invoices, details } };
      }
    );

    const checkedAll$ = checkedInvoices$.pipe(
      filter((rowsStatus) => rowsStatus.status === "SUCCESS"),
      map((rowsStatus) => rowsStatus.data.details),
      map((rows) => {
        return rows.filter((row) => !row.invoice.disabledCheckbox);
      }),
      map((rows) => {
        return rows.every((row) => row.checked);
      }),
      tap((checkedAll) => {
        setCheckedAll(checkedAll);
      })
    );

    const checkedAllSubscription = checkedAll$.subscribe();

    const checkedInvoicesSubscription = checkedInvoices$
      .pipe(
        tap((invoicesStatus) => {
          setInvoicesStatus({
            status: invoicesStatus.status,
            data: invoicesStatus.data,
            error: invoicesStatus.error,
            params: invoicesStatus.params,
          });
        })
      )
      .subscribe();

    const total$ = shoppingCar.items.valueChanges.pipe(
      map((items: any) => {
        return items.reduce(
          (t, e) => {
            return { pen: t.pen + e.amount.pen, usd: t.usd + e.amount.usd };
          },
          {
            pen: 0,
            usd: 0,
          }
        );
      })
    );

    const totalSubscription = total$
      .pipe(
        tap((total: any) => {
          setTotal(total);
        })
      )
      .subscribe();

    return () => {
      checkedInvoicesSubscription.unsubscribe();
      checkedAllSubscription.unsubscribe();
      totalSubscription.unsubscribe();
    };
  }, [loadInvoices.state$, shoppingCar]);

  const [checkedAll, setCheckedAll] = useState(false);
  const [useDollars] = useContext(CurrencyContext);
  const titles = getPaymentTableHeaders(useDollars);
  const gridTemplateColumns = "4rem 1fr 1fr 1fr 1fr 1fr 1.5fr 1fr 0.9fr";
  const [showModal, updateShowModal] = useState(false);

  const [showModalSelectInvoice, setShowModalSelectInvoice] = useState(false);

  const [showModalThirdError, setShowModalThirdError] = useState(false);
  const [messageErrorThird, setMessageErrorThird] = useState("");
  const [showModalThirdSuccess, setShowModalThirdSuccess] = useState(false);

  const [messageError1, updateMessageError1] = useState("");
  const [showModalError1, setShowModalError1] = useState(false);
  const [messageSuccessCreditMethod, setMessageSuccessCreditMethod] =
    useState("");

  const [modalSuccessCreditMethod, setModalSuccessCreditMethod] =
    useState(false);

  const history = useHistory();
  const [showModalUpfront, setShowModalUpfront] = useState<any>(false);
  const [messageErrorUpfront, setMessageErrorUpfront] = useState("");

  const [showModalDeleteSuccess, setShowModalDeleteSuccess] = useState(false);
  const [messageDeleteSuccess, setMessageDeleteSuccess] = useState("");
  const [titleMessageDeleteSuccess, setTitleMessageDeleteSuccess] =
    useState("");
  const [showModalSuccessContadoMethod, setShowModalSuccessContadoMethod] =
    useState(false);
  const [showModalErrorContadoMethod, setShowModalErrorContadoMethod] =
    useState(false);
  const [shoModalMessageContadoMethod, setShoModalMessageContadoMethod] =
    useState("");

  const [countNextPage, setCountNextPage] = useState(0);

  const [showErrorQuoted, setShowErrorQuoted] = useState("");

  const [statusSidebarInfo, setStatusSidebarInfo] = useState();
  const [showStatusSidebar, setShowStatusSidebar] = useState(false);
  const handleClick = (newSidebarInfo) => {
    setStatusSidebarInfo(newSidebarInfo);
    setShowStatusSidebar(true);
  };

  const [showModalSuccessReprogress, setShowModalSuccessReprogress] =
    useState(false);
  const [showErrorMessageReprogress, setShowErrorMessageReprogress] =
    useState("");

  const parsedParamsSide = { ...parse(window.location.search), page: 1 };
  const stringifiedParamsSide = stringify(parsedParamsSide);
  const pathname = history.location.pathname;
  return (
    <>
      {showStatusSidebar && (
        <PaymentQuotedSidebarStatus
          onOutsideClick={() => {
            setShowStatusSidebar(false);
          }}
          statusSidebarInfo={statusSidebarInfo}
          onSucessReprogress={() => {
            setShowStatusSidebar(false);
            setShowModalSuccessReprogress(true);
          }}
          onErrorReprogress={(error: string) => {
            setShowStatusSidebar(false);
            setShowErrorMessageReprogress(error);
          }}
        />
      )}

      <ModalSuccessConfirmation
        title="Reprocesado exitoso"
        message="Ya puedes realizar el pago de tu prefactura."
        showProcessing={showModalSuccessReprogress}
        onAccept={() => {
          setShowModalSuccessReprogress(false);

          history.push(`${pathname}?${stringifiedParamsSide}`);
        }}
        onClose={() => {
          setShowModalSuccessReprogress(false);
          history.push(`${pathname}?${stringifiedParamsSide}`);
        }}
        buttonLabel="Entendido"
      />
      {showErrorMessageReprogress && (
        <ConfirmationModal
          message={showErrorMessageReprogress}
          onAccept={() => {
            setShowErrorMessageReprogress("");
          }}
          title={"Atención"}
          toggleModal={() => {
            setShowErrorMessageReprogress("");
          }}
        />
      )}

      {showModalDeleteSuccess && messageDeleteSuccess && (
        <ConfirmationModal
          message={messageDeleteSuccess}
          onAccept={() => {
            setMessageDeleteSuccess("");
            setTitleMessageDeleteSuccess("");
            setShowModalDeleteSuccess(false);
            const parsedParams = { ...parse(window.location.search), page: 1 };
            const stringifiedParams = stringify(parsedParams);
            const pathname = history.location.pathname;
            history.push(`${pathname}?${stringifiedParams}`);
          }}
          title={titleMessageDeleteSuccess}
          toggleModal={() => {
            setMessageDeleteSuccess("");
            setTitleMessageDeleteSuccess("");
            setShowModalDeleteSuccess(false);
            const parsedParams = { ...parse(window.location.search), page: 1 };
            const stringifiedParams = stringify(parsedParams);
            const pathname = history.location.pathname;
            history.push(`${pathname}?${stringifiedParams}`);
          }}
        />
      )}
      {showModalThirdError && messageErrorThird && (
        <ConfirmationModal
          message={messageErrorThird}
          onAccept={() => {
            setShowModalThirdError(false);
            setMessageErrorThird("");
          }}
          title={`Atención`}
          toggleModal={() => {
            setShowModalThirdError(false);
            setMessageErrorThird("");
          }}
        />
      )}
      {showModalThirdSuccess && (
        <ConfirmationModal
          message={
            "La carga de los documentos ha sido exitosa. Haga click en entendido o cierre el dialogo para continuar."
          }
          onAccept={() => {
            setShowModalThirdSuccess(false);
            clearShoppingCar();
            const parsedParams = { ...parse(window.location.search), page: 1 };
            const stringifiedParams = stringify(parsedParams);
            const pathname = history.location.pathname;
            if (countNextPage > 0) {
              history.push(`${pathname}?${stringifiedParams}`);
            } else {
              history.push(USER_ROUTES.PAYMENT_VALIDATION_STATUS.path);
            }
          }}
          title={`Documentos subidos con éxito`}
          toggleModal={() => {
            setShowModalThirdSuccess(false);
            clearShoppingCar();
            const parsedParams = { ...parse(window.location.search), page: 1 };
            const stringifiedParams = stringify(parsedParams);
            const pathname = history.location.pathname;
            if (countNextPage > 0) {
              history.push(`${pathname}?${stringifiedParams}`);
            } else {
              history.push(USER_ROUTES.PAYMENT_VALIDATION_STATUS.path);
            }
          }}
        />
      )}
      {showModalSuccessContadoMethod && (
        <ConfirmationModal
          message={
            "Su pago ha sido confirmado exitosamente, espere a que sea validado para poder terminar la facturación."
          }
          onAccept={() => {
            setShowModalSuccessContadoMethod(false);
            clearShoppingCar();
            const parsedParams = { ...parse(window.location.search), page: 1 };
            const stringifiedParams = stringify(parsedParams);
            const pathname = history.location.pathname;
            if (countNextPage > 0) {
              history.push(`${pathname}?${stringifiedParams}`);
            } else {
              history.push(USER_ROUTES.PAYMENT_VALIDATION_STATUS.path);
            }
          }}
          title={`Pago confirmado con éxito`}
          toggleModal={() => {
            setShowModalSuccessContadoMethod(false);
            clearShoppingCar();
            const parsedParams = { ...parse(window.location.search), page: 1 };
            const stringifiedParams = stringify(parsedParams);
            const pathname = history.location.pathname;
            history.push(`${pathname}?${stringifiedParams}`);
          }}
        />
      )}

      {showModalErrorContadoMethod && shoModalMessageContadoMethod && (
        <ConfirmationModal
          message={shoModalMessageContadoMethod}
          onAccept={() => {
            setShoModalMessageContadoMethod("");
            setShowModalErrorContadoMethod(false);
          }}
          title={`Atención`}
          toggleModal={() => {
            setShoModalMessageContadoMethod("");
            setShowModalErrorContadoMethod(false);
          }}
        />
      )}

      <InvoicesContainer gridTemplateColumns={gridTemplateColumns}>
        {invoicesStatus.status === "PROCESSING" && (
          <>
            <PaymentLoader numItems={ITEMS_PER_PAGE} />
          </>
        )}
        {invoicesStatus.status === "SUCCESS" && (
          <>
            {invoicesStatus.data.details.length === 0 && (
              <StandaloneMessage>
                No hay ningún volante prefacturado.
              </StandaloneMessage>
            )}

            {invoicesStatus.data.details.length > 0 && (
              <>
                <TableHeader className="item-container">
                  <DivCheckbox disabled={invoicesStatus.data?.disabledAll}>
                    <Checkbox
                      className="checkInvoice clickable-element"
                      type="checkbox"
                      onChange={(e) => {
                        if (e.target.checked) {
                          invoicesStatus.data.details
                            .filter((item) => !item.invoice.disabledCheckbox)
                            .forEach((item) => {
                              const { invoice } = item;
                              shoppingCar.addItem(invoice);
                            });
                        } else {
                          invoicesStatus.data.details
                            .filter((item) => !item.invoice.disabledCheckbox)
                            .forEach((item) => {
                              const { invoice } = item;
                              shoppingCar.removeItem(invoice.id);
                            });
                        }
                      }}
                      checked={!invoicesStatus.data?.disabledAll && checkedAll}
                      disabled={invoicesStatus.data?.disabledAll}
                    />
                  </DivCheckbox>
                  <div className="main-info-header">{titles.importer}</div>

                  <div>{titles.volante}</div>
                  <div>{titles.status}</div>
                  <div>{titles.retirement}</div>
                  <div className="main-info-header">{titles.rucId}</div>
                  <div>{titles.amount}</div>
                  <div style={{ marginRight: "2rem" }}>
                    {titles.statusPreInvoice}
                  </div>
                  <div>{titles.actions}</div>
                </TableHeader>
                {invoicesStatus.data.details.map((item) => {
                  const { checked, invoice } = item;
                  const {
                    consigneeRuc,
                    importer,
                    volanteId,
                    withdrawalDate,
                    ruc,
                    amount,
                    disabledCheckbox,
                    status,
                  } = invoice;
                  const { problems } = status;
                  const numberProblems = problems.length < 1;

                  const today = moment(
                    new Date(new Date().setHours(0, 0, 0, 0))
                  );
                  return (
                    <div key={invoice.id}>
                      <InvoiceContainer className="item-container">
                        <div className="attribute">
                          <div className="titles">{titles.select}</div>
                          <DivCheckbox disabled={disabledCheckbox}>
                            <Checkbox
                              className="checkbox-react-prime-class"
                              checked={checked}
                              onChange={(e) => {
                                if (e.checked) {
                                  shoppingCar.addItem(invoice);
                                } else {
                                  shoppingCar.removeItem(invoice.id);
                                }
                              }}
                              disabled={disabledCheckbox}
                            />
                          </DivCheckbox>
                        </div>
                        <div className="attribute main-info-header">
                          <div className="titles">{titles.importer}</div>
                          <div className="main-info-header">
                            <p className="id">{consigneeRuc}</p>
                            <p>{importer}</p>
                          </div>
                        </div>

                        <div className="attribute">
                          <div className="titles">{titles.volante}</div>
                          <VolanteId>{volanteId}</VolanteId>
                        </div>

                        <div className="attribute">
                          <div className="titles">{titles.status}</div>
                          <StatusSectionDetails invoice={invoice} />
                        </div>
                        <div className="attribute">
                          <div className="titles">{titles.retirement}</div>
                          <ColoredTag
                            hasExpired={moment(withdrawalDate) < today}
                          >
                            {dateToRegularFormat(withdrawalDate)}
                          </ColoredTag>
                        </div>
                        <div className="attribute main-info-header">
                          <div className="titles">{titles.rucId}</div>
                          <div className="main-info-header">
                            <p className="ruc-id">{ruc.ruc}</p>
                            <p>{ruc.name}</p>
                          </div>
                        </div>
                        <div className="attribute">
                          <div className="titles">{titles.amount}</div>
                          <div>
                            {amount.pen === 0 || amount.pen === 0 ? (
                              <div>-</div>
                            ) : (
                              <div>{formatMoney(amount, useDollars)}</div>
                            )}
                          </div>
                        </div>
                        <div className="attribute" style={{}}>
                          <div className="titles">
                            {titles.statusPreInvoice}
                          </div>
                          <div
                            style={{
                              cursor: `${
                                invoice.quotation_process_status === "failed" &&
                                numberProblems
                                  ? "pointer"
                                  : "default"
                              }`,
                            }}
                            onClick={() => {
                              if (
                                invoice.quotation_process_status === "failed" &&
                                numberProblems
                              ) {
                                handleClick(invoice);
                              }
                            }}
                          >
                            {invoice.quotation_process_status === "pending" ? (
                              <ProcessingColum>
                                <div>PROCESANDO</div>
                                <img
                                  src={GifPending}
                                  alt="figPending"
                                  className="img-waiting"
                                />
                              </ProcessingColum>
                            ) : (
                              <div>
                                <StatusCommon
                                  label={invoice.volanteStatus.label}
                                  status={invoice.volanteStatus.category}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        <PaymentQuotedModals
                          invoice={invoice}
                          refreshPage={() => {
                            loadInvoices.retry();
                          }}
                          actions={titles.actions}
                          setShowModalDeleteSuccess={setShowModalDeleteSuccess}
                          setMessageDeleteSuccess={setMessageDeleteSuccess}
                          setTitleMessageDeleteSuccess={
                            setTitleMessageDeleteSuccess
                          }
                        />
                      </InvoiceContainer>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </InvoicesContainer>

      {invoicesStatus.status === "SUCCESS" && (
        <>
          {invoicesStatus.data.count > 0 && (
            <Container>
              <Authorization actions={["quotation.get_quotation_quoted"]}>
                <div className="flex-paginator">
                  <Paginator
                    first={
                      (invoicesStatus.params.page - 1) *
                      invoicesStatus.data.page_size
                    }
                    rows={invoicesStatus.data.page_size}
                    totalRecords={invoicesStatus.data.count}
                    onPageChange={(e) => {
                      const targetPage =
                        e.first / invoicesStatus.data.page_size;
                      const pathname = history.location.pathname;
                      const parsedParams = parse(history.location.search);
                      const stringifiedParams = stringify({
                        ...parsedParams,
                        page: targetPage + 1,
                      });
                      history.push(`${pathname}?${stringifiedParams}`);
                    }}
                  />
                </div>
              </Authorization>
              <div>
                <ButtonContainer>
                  <Authorization
                    actions={[
                      "invoice.get_invoice_estimate_upfront_manual",
                      "invoice.get_invoice_estimate_credit",
                      "invoice.get_invoice_estimate_upfront",
                    ]}
                  >
                    <MainButton
                      disabled={
                        shoppingCar.items.value.length === 0 ||
                        loadInvoices.isLoading ||
                        showModal
                      }
                      onClick={() => {
                        updateShowModal(!showModal);
                        setShowModalSelectInvoice(true);
                      }}
                    >
                      {`Realizar pago (`}
                      {formatMoney(total, useDollars)}
                      {`)`}
                    </MainButton>
                  </Authorization>
                </ButtonContainer>
              </div>
            </Container>
          )}
        </>
      )}

      {showModalUpfront && messageErrorUpfront && (
        <ConfirmationModal
          message={messageErrorUpfront}
          onAccept={() => {
            setMessageErrorUpfront("");
            showModalUpfront(false);
          }}
          title={`Atención`}
          toggleModal={() => {
            setMessageErrorUpfront("");
            showModalUpfront(false);
          }}
        />
      )}

      {showModal && (
        <PaymentModal
          toggleModal={(refresh) => {
            if (refresh) {
              loadInvoices.retry();
              shoppingCar.items.clear();
            }

            updateShowModal(false);
          }}
          invoices={shoppingCar.items.value}
          hideModalPaymentType={() => {
            updateShowModal(false);
          }}
          hideModalPaymentTypeShowModal={showModal}
          updateMessageError1={updateMessageError1}
          setShowModalError1={setShowModalError1}
          setMessageSuccessCreditMethod={setMessageSuccessCreditMethod}
          setModalSuccessCreditMethod={setModalSuccessCreditMethod}
          setShowModalUpfront={setShowModalUpfront}
          setMessageErrorUpfront={setMessageErrorUpfront}
          updateShowModal={updateShowModal}
          setShowModalThirdError={setShowModalThirdError}
          setMessageErrorThird={setMessageErrorThird}
          setShowModalThirdSuccess={setShowModalThirdSuccess}
          showModalSelectInvoice={showModalSelectInvoice}
          setShowModalSuccessContadoMethod={setShowModalSuccessContadoMethod}
          setShowModalErrorContadoMethod={setShowModalErrorContadoMethod}
          setShoModalMessageContadoMethod={setShoModalMessageContadoMethod}
          countPageRedirect={(data) => {
            setCountNextPage(data);
          }}
          dateUrlQuotationQuoted={dateUrlQuotationQuoted}
          closeModal={() => {
            updateShowModal(false);
            shoppingCar.items.clear();
          }}
          onErrorQuoted={(data) => {
            setShowErrorQuoted(data);
            updateShowModal(false);
            shoppingCar.items.clear();
          }}
          clearShoppingCar={() => {
            shoppingCar.items.clear();
          }}
        />
      )}
      {showErrorQuoted && (
        <ConfirmationModal
          message={showErrorQuoted}
          onAccept={() => {
            setShowErrorQuoted("");
            shoppingCar.items.clear();
          }}
          title={`Atención`}
          toggleModal={() => {
            setShowErrorQuoted("");
            shoppingCar.items.clear();
          }}
        />
      )}
      {showModalError1 && messageError1 && (
        <ConfirmationModal
          message={messageError1}
          onAccept={() => {
            updateMessageError1("");
            setShowModalError1(false);
            updateShowModal(false);
          }}
          title={`Atención`}
          toggleModal={() => {
            updateMessageError1("");
            setShowModalError1(false);
            updateShowModal(false);
          }}
        />
      )}
      {modalSuccessCreditMethod && messageSuccessCreditMethod && (
        <ConfirmationModal
          title={"Facturación al crédito confirmada"}
          toggleModal={() => {
            shoppingCar.items.clear();
            setMessageSuccessCreditMethod("");
            setModalSuccessCreditMethod(false);
            updateShowModal(false);
            const parsedParams = { ...parse(window.location.search), page: 1 };
            const stringifiedParams = stringify(parsedParams);
            const pathname = history.location.pathname;
            if (countNextPage > 0) {
              history.push(`${pathname}?${stringifiedParams}`);
            } else {
              history.push(USER_ROUTES.PAYMENT_VALIDATION_STATUS.path);
            }
          }}
          message={messageSuccessCreditMethod}
          onAccept={() => {
            shoppingCar.items.clear();
            setMessageSuccessCreditMethod("");
            setModalSuccessCreditMethod(false);
            updateShowModal(false);
            const parsedParams = { ...parse(window.location.search), page: 1 };
            const stringifiedParams = stringify(parsedParams);
            const pathname = history.location.pathname;
            if (countNextPage > 0) {
              history.push(`${pathname}?${stringifiedParams}`);
            } else {
              history.push(USER_ROUTES.PAYMENT_VALIDATION_STATUS.path);
            }
          }}
        />
      )}
    </>
  );
};

const PaymentQuotedModals = (props) => {
  const {
    invoice,
    refreshPage,
    actions,
    setShowModalDeleteSuccess,
    setMessageDeleteSuccess,
    setTitleMessageDeleteSuccess,
  } = props;

  const togglePaymentDeletionModal = () => {
    updatetModalState(
      modalState === PAYMENT_TABLE_MODAL_STATES.disabled
        ? PAYMENT_TABLE_MODAL_STATES.paymentDeletion
        : PAYMENT_TABLE_MODAL_STATES.disabled
    );
  };

  const togglePaymentCommentModal = () => {
    updatetModalState(
      modalState === PAYMENT_TABLE_MODAL_STATES.disabled
        ? PAYMENT_TABLE_MODAL_STATES.paymentComment
        : PAYMENT_TABLE_MODAL_STATES.disabled
    );
  };

  const [modalState, updatetModalState] = useState(
    PAYMENT_TABLE_MODAL_STATES.disabled
  );

  const {
    quotationComment,
    volanteId,
    id,
    quotationNum,
    quotationRejectedComment,
    limitQuotationComment,
    editCommentVolante,
    deleteVolante,
  } = invoice;
  const filledComment =
    typeof quotationComment === "string" && quotationComment !== "";

  const toggleCommentModal = (refreshNeeded) => {
    if (refreshNeeded) {
      refreshPage(["pending_validation", "rejected", "pending_payment"]);
    }
    updatetModalState(
      modalState === PAYMENT_TABLE_MODAL_STATES.disabled
        ? PAYMENT_TABLE_MODAL_STATES.comment
        : PAYMENT_TABLE_MODAL_STATES.disabled
    );
  };

  const saveComments = useAsyncExecutor(
    (params: any) => {
      const { id } = params;
      return networkBackendRequest("/quotations/", HTTP_METHODS.PATCH, null, {
        quotation_ids: [id],
      });
    },
    {
      onSuccess: () => {
        toggleCommentModal(true);
      },
    }
  );

  return (
    <>
      <div className="attribute">
        <div className="titles">{actions}</div>
        <div className="actions">
          <Authorization actions={["quotation.delete_quotation"]}>
            <DeleteButton
              tooltipText="Eliminar prefactura"
              onClick={() => {
                togglePaymentDeletionModal();
              }}
              disabled={!deleteVolante}
            />
          </Authorization>
          <Authorization actions={["quotation.update_quotation"]}>
            <CommentButton
              tooltipText={
                filledComment ? "Ver comentario" : "Agregar comentario"
              }
              isFilled={filledComment}
              onClick={() => {
                togglePaymentCommentModal();
              }}
              disabled={!editCommentVolante}
            />
          </Authorization>
        </div>
      </div>

      {modalState === PAYMENT_TABLE_MODAL_STATES.paymentComment ? (
        <PaymentCommentModal
          toggleModal={togglePaymentCommentModal}
          index={volanteId}
          quotationId={id}
          quotationComment={quotationComment}
          limitQuotationComment={limitQuotationComment}
          refreshPage={() => {
            refreshPage();
          }}
        />
      ) : modalState === PAYMENT_TABLE_MODAL_STATES.paymentDeletion ? (
        <PaymentDeletionModal
          toggleModal={togglePaymentDeletionModal}
          quotationNum={quotationNum}
          quotationId={id}
          refreshPage={() => {
            refreshPage(["quoted"]);
          }}
          setShowModalDeleteSuccess={setShowModalDeleteSuccess}
          setMessageDeleteSuccess={setMessageDeleteSuccess}
          setTitleMessageDeleteSuccess={setTitleMessageDeleteSuccess}
          updatetModalState={updatetModalState}
        />
      ) : modalState === PAYMENT_TABLE_MODAL_STATES.comment ? (
        <CommentModal
          waitingForServerResponse={saveComments.isLoading}
          toggleModal={toggleCommentModal}
          title={`Pago rechazado`}
          message={quotationRejectedComment}
          buttonText="Volver a generar prefactura"
          onAccept={() => {
            saveComments.execute({ id });
          }}
        />
      ) : null}
    </>
  );
};
